import { FormCard } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Select as AntdSelect, Checkbox, Form, InputNumber } from 'antd'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { RateType } from '../../hooks/usePaymentCards'
import {
  getRevisionTeamMembersSource,
  getTeamMemberDisplayName,
  normalizeTeamMemberFormIds,
  type TeamMemberPayRateOverrideForm
} from '../utils'

type OverrideTeamMemberPayRatesSectionProps = {
  revisionDetail: any
  payRatesEnabled: boolean
  payRateOverridesDisabled?: boolean
}

const getRateTypeOptions = (t: (key: string) => string) => [
  { value: RateType.HOURLY, label: t('RATE_TYPE_HOURLY') },
  { value: RateType.EVENT, label: t('RATE_TYPE_FIXED') }
]

const OverrideTeamMemberPayRatesSection = ({
  revisionDetail,
  payRatesEnabled,
  payRateOverridesDisabled = false
}: OverrideTeamMemberPayRatesSectionProps) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const teamMembers = Form.useWatch('teamMembers', form)
  const rateTypeOptions = useMemo(() => getRateTypeOptions(t), [t])

  const assignedIds = useMemo(
    () => normalizeTeamMemberFormIds(teamMembers),
    [teamMembers]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!payRatesEnabled) {
      return
    }
    const prev =
      (form.getFieldValue('teamMemberPayRateOverrides') as
        | Record<string, TeamMemberPayRateOverrideForm>
        | undefined) ?? {}
    let changed = false
    const next = { ...prev }
    for (const id of assignedIds) {
      if (!(id in next)) {
        next[id] = {
          overridePayRate: false,
          customRate: undefined,
          rateType: RateType.HOURLY
        }
        changed = true
      }
    }
    for (const key of Object.keys(next)) {
      if (!assignedIds.includes(key)) {
        delete next[key]
        changed = true
      }
    }
    if (changed) {
      form.setFieldsValue({ teamMemberPayRateOverrides: next })
    }
  }, [payRatesEnabled, assignedIds, form])

  if (!payRatesEnabled || assignedIds.length === 0) {
    return null
  }

  return (
    <FormCard
      id="rota-override-team-pay-rates"
      level={2}
      title={t('OVERRIDE_TEAM_MEMBER_PAY_RATES_TITLE')}
    >
      <IntroText>{t('OVERRIDE_TEAM_MEMBER_PAY_RATES_DESCRIPTION')}</IntroText>
      <RowsGap>
        {assignedIds.map((teamMemberId) => {
          const displayName = getTeamMemberDisplayName(
            teamMemberId,
            teamMembers,
            getRevisionTeamMembersSource(revisionDetail)
          )
          return (
            <MemberRow key={teamMemberId}>
              <Form.Item
                name={[
                  'teamMemberPayRateOverrides',
                  teamMemberId,
                  'overridePayRate'
                ]}
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox disabled={payRateOverridesDisabled} />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev?.teamMemberPayRateOverrides?.[teamMemberId]
                    ?.overridePayRate !==
                  next?.teamMemberPayRateOverrides?.[teamMemberId]
                    ?.overridePayRate
                }
              >
                {() => {
                  const enabled = Boolean(
                    form.getFieldValue([
                      'teamMemberPayRateOverrides',
                      teamMemberId,
                      'overridePayRate'
                    ])
                  )
                  const controlsEnabled = enabled && !payRateOverridesDisabled
                  return (
                    <>
                      <NameContainer $active={controlsEnabled}>
                        <NameText $checked={enabled}>{displayName} :</NameText>
                      </NameContainer>
                      <Form.Item
                        name={[
                          'teamMemberPayRateOverrides',
                          teamMemberId,
                          'customRate'
                        ]}
                        rules={[
                          {
                            validator: async (_, value) => {
                              if (!controlsEnabled) {
                                return
                              }
                              if (
                                value == null ||
                                value === '' ||
                                Number.isNaN(Number(value))
                              ) {
                                throw new Error(
                                  t('OVERRIDE_TEAM_MEMBER_PAY_RATE_REQUIRED')
                                )
                              }
                            }
                          }
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          min={0}
                          max={99.99}
                          precision={2}
                          disabled={!controlsEnabled}
                          addonAfter={
                            <Form.Item
                              name={[
                                'teamMemberPayRateOverrides',
                                teamMemberId,
                                'rateType'
                              ]}
                              noStyle
                            >
                              <RateTypeSelect
                                options={rateTypeOptions}
                                disabled={!controlsEnabled}
                              />
                            </Form.Item>
                          }
                          style={{ width: 260 }}
                        />
                      </Form.Item>
                    </>
                  )
                }}
              </Form.Item>
            </MemberRow>
          )
        })}
      </RowsGap>
    </FormCard>
  )
}

const RateTypeSelect = styled(AntdSelect)`
  &.ant-select {
    width: 120px;
  }
`

const NameText = styled(Text)<{ $checked: boolean }>`
  color: ${({ theme, $checked }) =>
    $checked ? theme.color.neutral.color1 : theme.color.neutral.color5};
`
const IntroText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const RowsGap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`

const MemberRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`

const NameContainer = styled.span<{ $active: boolean }>`
  flex: 0 0 auto;
  min-width: 256px;
  padding-top: 5px;
  color: ${({ theme, $active }) =>
    $active ? theme.color.neutral.color3 : theme.color.neutral.color5};
`

export default OverrideTeamMemberPayRatesSection
